<template>
  <div class="flex flex-col items-center justify-between gap-3 p-3 bg-white rounded-lg shadow-sm">
    <!-- Records per page selector and pagination info -->
    <div class="flex items-center justify-between w-full gap-4">
      <!-- Records per page selector -->
      <div v-if="showRecordsPerPage" class="flex items-center gap-2">
        <span class="text-sm text-gray-600">{{ $t('reports.pagination.records_per_page') }}:</span>
        <select
          v-model="selectedLimit"
          @change="onRecordsPerPageChange"
          class="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          <option v-for="limit in availableLimits" :key="limit" :value="limit">
            {{ limit }}
          </option>
        </select>
      </div>
      
      <!-- Pagination info -->
      <div v-if="showPaginationInfo && (effectiveTotalCount > 0 || isLoading)" class="flex items-center gap-3 text-sm text-gray-600">
        <span v-if="effectiveTotalCount > 0">
          {{ $t('reports.pagination.showing') }} 
          <span class="font-medium">{{ startRecord }}</span> - 
          <span class="font-medium">{{ endRecord }}</span> 
          {{ $t('reports.pagination.of') }} 
          <span class="font-medium">{{ effectiveTotalCount }}</span>
        </span>
        <span v-else-if="isLoading && preservedTotalCount > 0" class="text-gray-500">
          {{ $t('reports.loading.processing') }}
        </span>
        
        <!-- Total records badge -->
        <span v-if="effectiveTotalCount > 0" class="!bg-primary-900 px-2 py-1 rounded text-white text-sm font-medium">
          {{ effectiveTotalCount }} {{ $t('reports.pagination.records') }}
        </span>
      </div>
    </div>
    
    <!-- Pagination controls -->
    <div v-if="showPaginationControls" class="flex items-center gap-1">
      <!-- First page -->
      <button
        @click="goToPage(1)"
        :disabled="currentPage === 1"
        class="px-2 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
        :title="$t('reports.pagination.first_page')"
      >
        <v-icon size="small">mdi-chevron-double-left</v-icon>
      </button>
      
      <!-- Previous page -->
      <button
        @click="goToPage(currentPage - 1)"
        :disabled="currentPage === 1"
        class="px-2 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
        :title="$t('reports.pagination.previous_page')"
      >
        <v-icon size="small">mdi-chevron-left</v-icon>
      </button>
      
      <!-- Page numbers -->
      <div class="flex items-center gap-1">
        <button
          v-for="page in visiblePages"
          :key="page"
          @click="goToPage(page)"
          :class="[
            'px-2 py-1 text-sm border rounded-md min-w-[32px] focus:outline-none focus:ring-2 focus:ring-primary-500',
            page === currentPage
              ? 'bg-primary-500 text-white border-primary-500'
              : 'border-gray-300 hover:bg-gray-50'
          ]"
        >
          {{ page }}
        </button>
      </div>
      
      <!-- Next page -->
      <button
        @click="goToPage(currentPage + 1)"
        :disabled="currentPage === totalPages"
        class="px-2 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
        :title="$t('reports.pagination.next_page')"
      >
        <v-icon size="small">mdi-chevron-right</v-icon>
      </button>
      
      <!-- Last page -->
      <button
        @click="goToPage(totalPages)"
        :disabled="currentPage === totalPages"
        class="px-2 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
        :title="$t('reports.pagination.last_page')"
      >
        <v-icon size="small">mdi-chevron-double-right</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
  name: 'ReportPagination',
  props: {
    currentPage: {
      type: Number,
      required: true
    },
    totalPages: {
      type: Number,
      required: true
    },
    totalCount: {
      type: Number,
      required: true
    },
    recordsPerPage: {
      type: Number,
      required: true
    },
    availableLimits: {
      type: Array,
      default: () => [10, 25, 50, 100]
    },
    showRecordsPerPage: {
      type: Boolean,
      default: true
    },
    showPaginationInfo: {
      type: Boolean,
      default: true
    },
    maxVisiblePages: {
      type: Number,
      default: 7
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['page-change', 'records-per-page-change'],
  setup(props, { emit }) {
    const selectedLimit = ref(props.recordsPerPage);
    const preservedTotalCount = ref(props.totalCount);
    
    // Preserve total count during loading states
    watch(() => props.totalCount, (newCount) => {
      if (newCount > 0) {
        preservedTotalCount.value = newCount;
      }
    });
    
    // Computed properties
    const showPaginationControls = computed(() => {
      return props.totalPages > 1;
    });
    
    const effectiveTotalCount = computed(() => {
      // Use preserved count during loading if current count is 0
      if (props.isLoading && props.totalCount === 0 && preservedTotalCount.value > 0) {
        return preservedTotalCount.value;
      }
      return props.totalCount;
    });
    
    const startRecord = computed(() => {
      return effectiveTotalCount.value > 0 ? (props.currentPage - 1) * props.recordsPerPage + 1 : 0;
    });
    
    const endRecord = computed(() => {
      return Math.min(props.currentPage * props.recordsPerPage, effectiveTotalCount.value);
    });
    
    const visiblePages = computed(() => {
      if (props.totalPages <= props.maxVisiblePages) {
        return Array.from({ length: props.totalPages }, (_, i) => i + 1);
      }
      
      const halfVisible = Math.floor(props.maxVisiblePages / 2);
      let start = Math.max(1, props.currentPage - halfVisible);
      let end = Math.min(props.totalPages, start + props.maxVisiblePages - 1);
      
      // Adjust start if we're near the end
      if (end === props.totalPages) {
        start = Math.max(1, end - props.maxVisiblePages + 1);
      }
      
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    });
    
    // Methods
    const goToPage = (page) => {
      if (page >= 1 && page <= props.totalPages && page !== props.currentPage) {
        console.log('ReportPagination: Navigating to page', page, 'Current total count:', props.totalCount, 'Preserved count:', preservedTotalCount.value);
        emit('page-change', page);
      }
    };
    
    const onRecordsPerPageChange = () => {
      console.log('ReportPagination: Records per page changed to:', selectedLimit.value);
      emit('records-per-page-change', selectedLimit.value);
    };
    
    // Watch for prop changes
    watch(() => props.recordsPerPage, (newValue) => {
      selectedLimit.value = newValue;
    });
    
    // Debug logging
    watch(() => props.totalCount, (newCount) => {
      console.log('ReportPagination: Total count changed to:', newCount, 'Loading:', props.isLoading, 'Preserved count:', preservedTotalCount.value);
    });
    
    watch(() => props.isLoading, (loading) => {
      console.log('ReportPagination: Loading state changed to:', loading, 'Total count:', props.totalCount, 'Effective count:', effectiveTotalCount.value);
    });
    
    return {
      selectedLimit,
      showPaginationControls,
      startRecord,
      endRecord,
      visiblePages,
      effectiveTotalCount,
      goToPage,
      onRecordsPerPageChange
    };
  }
};
</script>

<style scoped>
/* Add any custom styles here */
</style> 